import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Check, Heart, MessageSquare, Phone, Calendar, Loader2, CreditCard, Shield, Sparkles } from 'lucide-react';
import { BRAND_IMAGES } from '@/constants/brandImages';

const API_URL = window.location.origin;

// Ontraport Inner Circle payment form ID
const ONTRAPORT_FORM_UID = 'p2c260712f39';
const ONTRAPORT_FORM_PARAMS = 'borderColor=#000000&borderSize=0px&embed=true&formHeight=800&formWidth=100%&popPosition=mc&instance=126195367';

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const ontraportContainerRef = useRef(null);
  
  // Form state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [dobError, setDobError] = useState('');
  
  // UI state
  const [step, setStep] = useState(1); // 1 = info, 2 = payment
  const [formLoading, setFormLoading] = useState(false);
  const [paymentFormLoading, setPaymentFormLoading] = useState(true);
  
  // Gift membership state
  const [giftCode, setGiftCode] = useState(null);
  const [giftData, setGiftData] = useState(null);

  // Check for gift code on mount
  useEffect(() => {
    const code = searchParams.get('gift') || sessionStorage.getItem('gift_code');
    if (code) {
      setGiftCode(code);
      const storedEmail = sessionStorage.getItem('gift_email');
      const storedName = sessionStorage.getItem('gift_name');
      if (storedEmail) setEmail(storedEmail);
      if (storedName) setFullName(storedName);
      fetchGiftData(code);
    }
  }, [searchParams]);

  const fetchGiftData = async (code) => {
    try {
      const response = await axios.get(`${API_URL}/api/gift/validate/${code}`);
      setGiftData(response.data);
      if (response.data.recipient_email) setEmail(response.data.recipient_email);
      if (response.data.recipient_name) setFullName(response.data.recipient_name);
    } catch (error) {
      console.error('Gift validation error:', error);
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Validate DOB on change
  const handleDobChange = (e) => {
    const dob = e.target.value;
    setDateOfBirth(dob);
    
    if (dob) {
      const age = calculateAge(dob);
      if (age < 18) {
        setDobError('You must be 18 years or older to register');
      } else {
        setDobError('');
      }
    } else {
      setDobError('');
    }
  };

  // Load Ontraport payment form
  const loadOntraportForm = () => {
    if (ontraportContainerRef.current) {
      ontraportContainerRef.current.innerHTML = '';
      setPaymentFormLoading(true);
      
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = 'https://app.ontraport.com/js/ontraport/opt_assets/drivers/opf.js';
      script.setAttribute('data-opf-uid', ONTRAPORT_FORM_UID);
      script.setAttribute('data-opf-params', ONTRAPORT_FORM_PARAMS);
      
      script.onload = () => {
        setTimeout(() => setPaymentFormLoading(false), 1500);
      };
      
      script.onerror = () => {
        setPaymentFormLoading(false);
        toast.error('Failed to load payment form. Please refresh and try again.');
      };
      
      ontraportContainerRef.current.appendChild(script);
      
      const timeout = setTimeout(() => setPaymentFormLoading(false), 8000);
      return () => clearTimeout(timeout);
    }
  };

  // Handle step 1 submission - validate and proceed to payment
  const handleContinueToPayment = (e) => {
    e.preventDefault();
    
    // Validate full name
    if (!fullName || fullName.trim().length < 2) {
      toast.error('Please enter your full name');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Validate phone number
    if (!phoneNumber || phoneNumber.trim().length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    // Validate date of birth
    if (!dateOfBirth) {
      toast.error('Please enter your date of birth');
      return;
    }

    const age = calculateAge(dateOfBirth);
    if (age < 18) {
      toast.error('You must be 18 years or older to register');
      return;
    }

    // Terms acceptance is required
    if (!termsAccepted) {
      toast.error('Please accept the Terms of Service and Privacy Policy');
      return;
    }
    
    // Store registration data in localStorage for webhook to use
    const registrationData = {
      fullName,
      email,
      phoneNumber,
      dateOfBirth,
      smsOptIn,
      termsAcceptedAt: new Date().toISOString(),
      giftCode: giftCode || null
    };
    
    localStorage.setItem('pendingRegistration', JSON.stringify(registrationData));
    
    // If gift code, handle differently (gift memberships are pre-paid)
    if (giftCode && giftData) {
      handleGiftRegistration();
      return;
    }
    
    // Move to payment step
    setStep(2);
    
    // Load Ontraport form after a brief delay
    setTimeout(() => {
      loadOntraportForm();
    }, 100);
  };

  // Handle gift registration (no payment needed)
  const handleGiftRegistration = async () => {
    setFormLoading(true);
    try {
      // For gift registrations, we create the account directly
      // since payment was already made by the gifter
      const response = await axios.post(`${API_URL}/api/auth/register-gift`, {
        email,
        full_name: fullName,
        phone_number: phoneNumber,
        dob: dateOfBirth,
        notification_preference: smsOptIn ? 'both' : 'email',
        gift_code: giftCode
      });
      
      toast.success('Account created! Check your email for your login credentials.');
      localStorage.removeItem('pendingRegistration');
      sessionStorage.removeItem('gift_code');
      sessionStorage.removeItem('gift_email');
      sessionStorage.removeItem('gift_name');
      
      // Redirect to login page with a success message
      window.location.href = '/login?gift_activated=true';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const benefits = [
    'A sacred sisterhood that sees you',
    'Soul-centered coaching and guidance',
    'Safe spaces to speak what\'s sacred',
    'Restoration library for your wholeness',
    'Permission to rest and become'
  ];

  return (
    <div className="min-h-screen bg-cream flex">
      {/* Left Side - Branding with Image */}
      <div className="hidden lg:flex flex-1 hero-gradient items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={BRAND_IMAGES.wellness.joyful} 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-white max-w-lg">
          <h1 className="font-heading text-4xl md:text-5xl mb-6 leading-tight">
            You Deserve a Space Where You Don't Have to Prove Anything
          </h1>
          <p className="text-lg text-white/90 mb-8">
            Step into a sisterhood of brilliant Black women who see you, support you, and celebrate your becoming.
          </p>
          <ul className="space-y-3">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3 text-white/90">
                <Check className="h-5 w-5 text-gold shrink-0" />
                {benefit}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right Side - Registration Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 overflow-y-auto">
        <Link to="/" className="flex items-center gap-2 text-forest/70 hover:text-forest mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        <Card className="w-full max-w-md card-base" data-testid="register-card">
          {step === 1 ? (
            <>
              <CardHeader className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Heart className="h-6 w-6 text-terracotta" />
                  {giftCode && <span className="text-xs bg-gold/20 text-gold px-2 py-1 rounded-full">Gift Membership</span>}
                </div>
                <CardTitle className="font-heading text-2xl text-forest">
                  {giftCode ? 'Activate Your Gift' : 'Join Inner Circle'}
                </CardTitle>
                <p className="text-gray-600 text-sm mt-2">
                  {giftCode 
                    ? `${giftData?.gifter_name || 'Someone special'} has gifted you 30 days FREE!`
                    : 'First month FREE, then $97/month. Cancel anytime.'
                  }
                </p>
                {giftCode && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-left">
                    <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Card Required to Activate Your Free Trial
                    </p>
                    <ul className="text-xs text-amber-700 mt-2 space-y-1 ml-6">
                      <li>• Your card will <strong>NOT</strong> be charged during your 30-day trial</li>
                      <li>• Cancel anytime before trial ends — no obligation</li>
                      <li>• After 30 days, membership continues at $97/month</li>
                    </ul>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContinueToPayment} className="space-y-4">
                  {/* Full Name */}
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="Your full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                      data-testid="register-name-input"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="register-email-input"
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      required
                      data-testid="register-phone-input"
                    />
                    <p className="text-xs text-gray-500">Required for account recovery and important updates</p>
                  </div>

                  {/* Date of Birth */}
                  <div className="space-y-2">
                    <Label htmlFor="dob" className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Date of Birth *
                    </Label>
                    <Input
                      id="dob"
                      type="date"
                      value={dateOfBirth}
                      onChange={handleDobChange}
                      max={new Date().toISOString().split('T')[0]}
                      required
                      data-testid="register-dob-input"
                    />
                    {dobError ? (
                      <p className="text-xs text-red-500">{dobError}</p>
                    ) : (
                      <p className="text-xs text-gray-500">You must be 18 years or older to join</p>
                    )}
                  </div>

                  {/* SMS Notification Opt-In */}
                  <div className="flex items-start space-x-3 p-4 bg-sand/30 rounded-lg">
                    <Checkbox 
                      id="sms-optin"
                      checked={smsOptIn}
                      onCheckedChange={setSmsOptIn}
                      className="mt-0.5"
                      data-testid="sms-optin-checkbox"
                    />
                    <div className="space-y-1">
                      <Label 
                        htmlFor="sms-optin" 
                        className="text-sm font-medium cursor-pointer flex items-center gap-2"
                      >
                        <MessageSquare className="h-4 w-4 text-forest" />
                        Send me text message notifications
                      </Label>
                      <p className="text-xs text-gray-500">
                        Receive session reminders, community updates, and messages from your coach via SMS. 
                        You can change this anytime in Settings.
                      </p>
                    </div>
                  </div>

                  {/* Terms and Conditions Acceptance - Required */}
                  <div className="flex items-start space-x-3 p-4 bg-forest/5 rounded-lg border border-forest/20">
                    <Checkbox 
                      id="terms-acceptance"
                      checked={termsAccepted}
                      onCheckedChange={setTermsAccepted}
                      className="mt-0.5"
                      data-testid="terms-acceptance-checkbox"
                      required
                    />
                    <div className="space-y-1">
                      <Label 
                        htmlFor="terms-acceptance" 
                        className="text-sm font-medium cursor-pointer"
                      >
                        I agree to the Terms of Service and Privacy Policy <span className="text-red-500">*</span>
                      </Label>
                      <p className="text-xs text-gray-600">
                        By creating an account, I acknowledge that I have read and agree to the{' '}
                        <Link to="/terms" target="_blank" className="text-terracotta hover:underline font-medium">
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" target="_blank" className="text-terracotta hover:underline font-medium">
                          Privacy Policy
                        </Link>
                        , including the arbitration agreement and class action waiver.
                      </p>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={formLoading || !!dobError}
                    data-testid="continue-to-payment-btn"
                  >
                    {formLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : giftCode ? (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Activate Gift Membership
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Continue to Payment
                      </>
                    )}
                  </Button>

                  {!giftCode && (
                    <p className="text-center text-xs text-gray-500">
                      <Shield className="h-3 w-3 inline mr-1" />
                      Secure checkout powered by Ontraport. Cancel anytime.
                    </p>
                  )}
                </form>

                <p className="text-center text-sm text-gray-600 mt-6">
                  Already have an account?{' '}
                  <Link to="/login" className="text-forest font-medium hover:text-terracotta">
                    Sign in
                  </Link>
                </p>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="text-center">
                <CardTitle className="font-heading text-2xl text-forest">
                  Complete Your Membership
                </CardTitle>
                <p className="text-gray-600 text-sm mt-2">
                  Welcome, {fullName.split(' ')[0]}! Complete your payment to join Inner Circle.
                </p>
              </CardHeader>
              <CardContent>
                {/* Back button */}
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center gap-2 text-forest/70 hover:text-forest mb-4 text-sm transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to edit info
                </button>

                {/* Summary of info */}
                <div className="bg-sand/30 rounded-lg p-4 mb-6 text-sm">
                  <p><strong>Name:</strong> {fullName}</p>
                  <p><strong>Email:</strong> {email}</p>
                  <p><strong>Phone:</strong> {phoneNumber}</p>
                </div>

                {/* Ontraport Payment Form */}
                <div className="relative min-h-[400px]">
                  {paymentFormLoading && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-sand/50 rounded-lg z-10">
                      <Loader2 className="h-8 w-8 animate-spin text-forest mb-4" />
                      <p className="text-forest font-medium">Loading Checkout...</p>
                      <p className="text-sm text-gray-500">Preparing your free trial registration</p>
                    </div>
                  )}
                  <div 
                    ref={ontraportContainerRef}
                    className="ontraport-form-container"
                    data-testid="ontraport-payment-form"
                  />
                </div>

                <p className="text-center text-xs text-gray-500 mt-4">
                  <Shield className="h-3 w-3 inline mr-1" />
                  Your payment is secure. First month FREE, then $97/month. Cancel anytime.
                </p>
              </CardContent>
            </>
          )}
        </Card>

        {/* Mobile benefits */}
        <div className="lg:hidden mt-8 text-center text-gray-600">
          <p className="text-sm">Join hundreds of women on their journey to wholeness</p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
